import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Collapse,
  IconButton,
  Divider,
} from '@mui/material';
import { ExpandLess, ExpandMore, Search, SmartToy } from '@mui/icons-material';
import { SeoConfig } from '../../../pages/types';

interface SeoEditorProps {
  seo?: SeoConfig;
  onChange: (seo: SeoConfig) => void;
}

const SeoEditor: React.FC<SeoEditorProps> = ({ seo = {}, onChange }) => {
  const [seoOpen, setSeoOpen] = useState(true);
  const [aiOpen, setAiOpen] = useState(true);

  const update = (field: keyof SeoConfig, value: any) => {
    onChange({ ...seo, [field]: value });
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          py: 1,
          px: 2,
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
        }}
        onClick={() => setSeoOpen(!seoOpen)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Search fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold">
            SEO
          </Typography>
        </Box>
        <IconButton size="small">
          {seoOpen ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={seoOpen}>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            label="Page Title"
            fullWidth
            size="small"
            value={seo.title || ''}
            onChange={(e) => update('title', e.target.value)}
            placeholder="Defaults to menu title"
          />
          <TextField
            label="Meta Description"
            fullWidth
            size="small"
            multiline
            rows={3}
            value={seo.description || ''}
            onChange={(e) => update('description', e.target.value)}
            inputProps={{ maxLength: 160 }}
            helperText={`${(seo.description || '').length}/160`}
          />
          <TextField
            label="Keywords"
            fullWidth
            size="small"
            value={seo.keywords || ''}
            onChange={(e) => update('keywords', e.target.value)}
            placeholder="word1, word2, word3"
          />
          <TextField
            label="OG Image URL"
            fullWidth
            size="small"
            value={seo.ogImage || ''}
            onChange={(e) => update('ogImage', e.target.value)}
            placeholder="https://..."
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={seo.noIndex || false}
                onChange={(e) => update('noIndex', e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">Hide from search engines</Typography>
            }
          />
        </Box>
      </Collapse>

      <Divider />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          py: 1,
          px: 2,
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
        }}
        onClick={() => setAiOpen(!aiOpen)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToy fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold">
            AI
          </Typography>
        </Box>
        <IconButton size="small">
          {aiOpen ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={aiOpen}>
        <Box sx={{ p: 2 }}>
          <TextField
            label="AI Summary"
            fullWidth
            size="small"
            multiline
            rows={4}
            value={seo.aiSummary || ''}
            onChange={(e) => update('aiSummary', e.target.value)}
            placeholder="A concise summary of this page for AI assistants and LLMs..."
            helperText="Used in llms.txt and AI-friendly meta tags"
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default SeoEditor;
